/** Wertentwicklung / Risikokennzahlen table — TableData shape (Configure Table dialog). */

import { isSquadDynamicCell, type SquadDynamicCell } from './squadFondsdatenDynamic';

/** A cell is typed text or a dynamic config resolved from the fund's data (09.10.2026). */
export type PerformanceRiskCell = string | SquadDynamicCell;

export type PerformanceRiskTableData = {
  labels: string[];
  values: PerformanceRiskCell[][];
  columns?: Array<{ type?: 'text' | 'avatar' | 'link'; showValueConfiguration?: boolean }>;
};

/** Legacy shape used before Configure Table modal. */
export type PerformanceRiskLegacy = {
  labels?: string[];
  rows?: unknown[][];
  values?: unknown[][];
  columns?: Array<{ type?: 'text' | 'avatar' | 'link'; showValueConfiguration?: boolean }>;
};

const DEFAULT_LABELS = ['', '1 Monat', 'YTD', '1 Jahr', '3 Jahre', '5 Jahre', '10 Jahre', 'seit Auflage'];

/** Dash-only placeholders (`--`, `—`, `-`, …) → blank (SQUAD: empty cells show nothing). */
function blankIfEmptyPlaceholder(text: string): string {
  const t = text.trim();
  if (!t) return '';
  if (/^[-–—−]+$/.test(t)) return '';
  return text;
}

export const SQUAD_RISK_TABLE_SOURCES = {
  rendite: 'cixon_wertentwicklung_vergangen',
  renditePa: 'cixon_wertentwpa',
  volatilitaet: 'cixon_volatilitaet',
} as const;

/**
 * The fields are sent as `string`: the server then returns the stored fraction as is and
 * an empty string when it is missing. With `number`/`percentage` it multiplies only some
 * tables by 100 and prints a missing value as "0,00" — young share classes have no 3-, 5-
 * or 10-year figures, and those cells must stay empty.
 */
export function makeSquadRiskCell(table: string, field: string): SquadDynamicCell {
  return { isDynamic: true, fields: [{ type: 'table', table, field, valueType: 'string' }] };
}

const renditeCell = (field: string) => makeSquadRiskCell(SQUAD_RISK_TABLE_SOURCES.rendite, field);
const renditePaCell = (field: string) => makeSquadRiskCell(SQUAD_RISK_TABLE_SOURCES.renditePa, field);
const volaCell = (field: string) => makeSquadRiskCell(SQUAD_RISK_TABLE_SOURCES.volatilitaet, field);

/** Value cells per row, in the column order 1 Monat … seit Auflage. */
const DYNAMIC_ROWS: Record<'rendite' | 'renditePa' | 'volatilitaet', PerformanceRiskCell[]> = {
  rendite: ['m1', 'laufendesjahr', 'j1', 'j3', 'j5', 'j10', 'seitauflage'].map(renditeCell),
  renditePa: ['', '', '', renditePaCell('j3'), renditePaCell('j5'), renditePaCell('j10'), renditePaCell('seitauflage')],
  volatilitaet: ['m1', 'laufendesjahr', 'j1', 'j3', 'j5', 'j10', 'auflage'].map(volaCell),
};

const valueColumns = () => [
  { type: 'text' as const },
  ...Array.from({ length: DEFAULT_LABELS.length - 1 }, () => ({ type: 'text' as const, showValueConfiguration: true })),
];

/** Every figure from the fund's data (B1, 09.10.2026). The Configure Table dialog can still override cells. */
export const DEFAULT_PERFORMANCE_RISK_TABLE: PerformanceRiskTableData = {
  labels: DEFAULT_LABELS,
  values: [
    ['Rendite', ...DYNAMIC_ROWS.rendite],
    ['Rendite p.a.', ...DYNAMIC_ROWS.renditePa],
    ['Volatilität', ...DYNAMIC_ROWS.volatilitaet],
  ],
  columns: valueColumns(),
};

/**
 * The placeholder figures the table shipped with until 09.10.2026. They were never a
 * fund's data (the CIXON master printed 1 Jahr 11,20 % against a real 6,67 %), so a row
 * still holding exactly them is switched to the data on read. A row with anything typed
 * into it stays as it is.
 */
const LEGACY_PLACEHOLDER_ROWS: Array<{ values: string[]; dynamic: PerformanceRiskCell[] }> = [
  { values: ['2,14%', '4,85%', '11,20%', '8,92%', '9,44%', '7,18%', '6,85%'], dynamic: DYNAMIC_ROWS.rendite },
  { values: ['', '', '', '8,92%', '9,44%', '7,18%', '6,85%'], dynamic: DYNAMIC_ROWS.renditePa },
  { values: ['', '', '', '12,44%', '11,88%', '10,92%', '11,05%'], dynamic: DYNAMIC_ROWS.volatilitaet },
];

function migrateLegacyPlaceholderRow(row: PerformanceRiskCell[]): PerformanceRiskCell[] {
  const cells = row.slice(1);
  if (cells.length !== 7 || cells.some((c) => typeof c !== 'string')) return row;
  const match = LEGACY_PLACEHOLDER_ROWS.find((legacy) =>
    legacy.values.every((v, i) => v === String(cells[i]).trim()),
  );
  return match ? [row[0], ...match.dynamic] : row;
}

function cellValue(v: unknown): PerformanceRiskCell {
  if (v == null) return '';
  if (isSquadDynamicCell(v)) return v;
  let raw = '';
  if (typeof v === 'string' || typeof v === 'number') raw = String(v);
  else if (typeof v === 'object' && v !== null && 'text' in v) raw = String((v as any).text ?? '');
  else raw = String(v);
  return blankIfEmptyPlaceholder(raw);
}

/** Normalize saved data (TableData or legacy labels/rows) → TableData for dialog + render. */
export function normalizePerformanceRiskTable(raw: unknown): PerformanceRiskTableData {
  if (!raw || typeof raw !== 'object') return DEFAULT_PERFORMANCE_RISK_TABLE;

  const data = raw as PerformanceRiskLegacy;
  const labels = Array.isArray(data.labels) ? data.labels.map(String) : [...DEFAULT_LABELS];
  const rawRows = Array.isArray(data.values)
    ? data.values
    : Array.isArray(data.rows)
      ? data.rows
      : null;

  if (!rawRows || !rawRows.length) {
    return {
      ...DEFAULT_PERFORMANCE_RISK_TABLE,
      labels: labels.length ? labels : DEFAULT_PERFORMANCE_RISK_TABLE.labels,
    };
  }

  const values = rawRows.map((row) => migrateLegacyPlaceholderRow((row || []).map(cellValue)));
  const colCount = Math.max(
    labels.length,
    ...values.map((r) => r.length),
    DEFAULT_LABELS.length,
  );
  const nextLabels = [...labels];
  while (nextLabels.length < colCount) nextLabels.push('');

  const columns = Array.from({ length: colCount }, (_, i) => ({ type: 'text' as const, ...(data.columns?.[i] || {}) }));
  // The dialog shows a dynamic cell only in a column with value configuration switched on;
  // anywhere else it would render as an empty input and be overwritten as text.
  values.forEach((row) =>
    row.forEach((cell, i) => {
      if (isSquadDynamicCell(cell)) columns[i] = { ...columns[i], showValueConfiguration: true };
    }),
  );

  return {
    labels: nextLabels.slice(0, colCount),
    values: values.map((r) => {
      const next = [...r];
      while (next.length < colCount) next.push('');
      return next.slice(0, colCount);
    }),
    columns,
  };
}

/** Key of a cell in the fetched-values map. */
export const squadRiskCellKey = (row: number, col: number) => `${row}:${col}`;

/** Dynamic cells of the table, with their position. */
export function collectSquadRiskDynamicCells(
  table: PerformanceRiskTableData,
): Array<{ key: string; cell: SquadDynamicCell }> {
  const out: Array<{ key: string; cell: SquadDynamicCell }> = [];
  table.values.forEach((row, r) =>
    row.forEach((cell, c) => {
      if (isSquadDynamicCell(cell) && cell.fields?.length) out.push({ key: squadRiskCellKey(r, c), cell });
    }),
  );
  return out;
}

/**
 * A fetched fraction (e.g. "-0.018161") → "-1,82%". Returns and p.a. returns carry a "+"
 * like SQUAD's own factsheet; volatility has no sign. Only a single field of the three
 * sources fetched as text is formatted here; a cell set up otherwise in the dialog (another
 * table, valueType number/percentage — the server has formatted it already) is shown as
 * delivered.
 */
export function formatSquadRiskValue(cell: SquadDynamicCell, raw: string | undefined): string {
  const text = String(raw ?? '').trim();
  if (!text) return '';
  const tableFields = cell.fields.filter((f) => f.type === 'table');
  const field = tableFields.length === 1 ? tableFields[0] : undefined;
  const known = Object.values(SQUAD_RISK_TABLE_SOURCES) as string[];
  if (!field || !known.includes(String(field.table)) || field.valueType !== 'string') return text;
  const source = field.table;
  const n = Number(text.replace(',', '.'));
  if (!Number.isFinite(n)) return '';
  // Half away from zero, like Excel. toFixed(6) first: 0.00145 * 10000 is 14.499999999999998
  // in floating point and would round down. `|| 0` turns -0 into 0 so no "-0,00%" appears.
  const scaled = Number((Math.abs(n) * 10000).toFixed(6));
  const rounded = (Math.sign(n) * Math.round(scaled)) / 100 || 0;
  const formatted = `${rounded.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
  const signed = source !== SQUAD_RISK_TABLE_SOURCES.volatilitaet;
  return signed && rounded > 0 ? `+${formatted}` : formatted;
}

/** Table with every dynamic cell replaced by its formatted value (empty when not fetched). */
export function resolvePerformanceRiskTable(
  raw: unknown,
  values?: Map<string, string>,
): { labels: string[]; values: string[][]; columns?: PerformanceRiskTableData['columns'] } {
  const table = normalizePerformanceRiskTable(raw);
  return {
    labels: table.labels,
    columns: table.columns,
    values: table.values.map((row, r) =>
      row.map((cell, c) =>
        isSquadDynamicCell(cell) ? formatSquadRiskValue(cell, values?.get(squadRiskCellKey(r, c))) : cell,
      ),
    ),
  };
}

/** Shape used by preview/PDF table renderers (`labels` + `rows`). */
export function performanceRiskTableForRender(
  raw: unknown,
  values?: Map<string, string>,
): { labels: string[]; rows: string[][] } {
  const data = resolvePerformanceRiskTable(raw, values);
  return { labels: data.labels, rows: data.values };
}
